import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  'Comprar': 'bg-emerald-500/20 text-emerald-400',
  'Vender': 'bg-amber-500/20 text-amber-400',
  'Rentar': 'bg-blue-500/20 text-blue-400',
  'Mercado Guatemala': 'bg-purple-500/20 text-purple-400'
};

const RelatedArticles = ({ property, limit = 3, className = "" }) => {
  const { t } = useTranslation();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRelatedArticles();
  }, [property]);

  const fetchRelatedArticles = async () => {
    try {
      // Determine relevant category based on property type
      let category = null;
      if (property?.transaction_type === "Venta") {
        category = "Comprar";
      } else if (property?.transaction_type === "Alquiler") {
        category = "Rentar";
      }
      
      // Fetch articles, preferring the relevant category
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      params.append('limit', limit.toString());
      
      const response = await fetch(`${API_URL}/api/blog/articles?${params}`);
      const data = await response.json();
      
      let fetchedArticles = data.articles || [];
      
      // If not enough articles in category, fetch more general ones
      if (fetchedArticles.length < limit) {
        const generalResponse = await fetch(`${API_URL}/api/blog/articles?limit=${limit}`);
        const generalData = await generalResponse.json();
        const generalArticles = generalData.articles || [];
        
        // Merge without duplicates
        const existingIds = new Set(fetchedArticles.map(a => a.id));
        for (const article of generalArticles) {
          if (!existingIds.has(article.id) && fetchedArticles.length < limit) {
            fetchedArticles.push(article);
          }
        }
      }
      
      setArticles(fetchedArticles.slice(0, limit));
    } catch (error) {
      console.error('Error fetching related articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-GT', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className={`${className}`}>
        <h3 className="text-xl text-white font-medium mb-6">{t('relatedArticles.title', 'Artículos Relacionados')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-[#121212] border border-white/5 overflow-hidden">
              <Skeleton className="h-40 w-full bg-white/5" />
              <div className="p-4 space-y-3">
                <Skeleton className="h-4 w-20 bg-white/5" />
                <Skeleton className="h-5 w-full bg-white/5" />
                <Skeleton className="h-4 w-3/4 bg-white/5" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (articles.length === 0) {
    return null;
  }

  return (
    <div className={`${className}`} data-testid="related-articles">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl text-white font-medium">{t('relatedArticles.title', 'Artículos Relacionados')}</h3>
        <Link 
          to="/blog" 
          className="text-[#D4AF37] hover:text-[#FFE182] text-sm flex items-center gap-1 transition-colors"
        >
          {t('relatedArticles.viewAll', 'Ver todos')}
          <ArrowRight size={16} />
        </Link>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {articles.map((article) => (
          <Link 
            key={article.id}
            to={`/blog/${article.slug}`}
            className="group bg-[#121212] border border-white/5 hover:border-[#D4AF37]/30 overflow-hidden transition-all duration-300"
            data-testid={`related-article-${article.slug}`}
          >
            {/* Image */}
            {article.image_url && (
              <div className="relative h-40 overflow-hidden">
                <img 
                  src={article.image_url} 
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent opacity-60" />
              </div>
            )}
            
            {/* Content */}
            <div className="p-4">
              {/* Category & Date */}
              <div className="flex items-center gap-3 mb-3">
                <span className={`text-xs px-2 py-1 rounded ${CATEGORY_COLORS[article.category] || 'bg-white/10 text-white/70'}`}>
                  {article.category}
                </span>
                <span className="text-[#525252] text-xs flex items-center gap-1">
                  <Clock size={12} />
                  {article.read_time || 2} min
                </span>
              </div>
              
              {/* Title */}
              <h4 className="text-white font-medium line-clamp-2 group-hover:text-[#D4AF37] transition-colors">
                {article.title}
              </h4>
              
              {/* Excerpt */}
              {article.excerpt && (
                <p className="text-[#A3A3A3] text-sm mt-2 line-clamp-2">
                  {article.excerpt}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default RelatedArticles;
